import React, { useState, useEffect, useRef } from "react"
import { graphql } from "gatsby"
import { PrismicRichText, PrismicLink } from "@prismicio/react"
import { GatsbyImage } from "gatsby-plugin-image"
import { Modal } from "react-bootstrap"
import { idMaker } from "../utils/idMaker"
import { fireMetaEvent } from "../utils/useMeta"

export const HeroDark = ({ slice }) => {
  const [showModal, setShowModal] = useState(false)
  const formContainerRef = useRef(null)

  const handleOpenModal = () => {
    setShowModal(true)
    fireMetaEvent("Lead")
  }
  const handleCloseModal = () => setShowModal(false)

  const formScriptUrl = slice.primary.form_embed_url?.url

  // Listen for form submission messages from the embedded external form script
  useEffect(() => {
    function handleMessage(event) {
      const d = event.data

      // HubSpot
      if (d?.type === "hsFormCallback" && d?.eventName === "onFormSubmit") {
        const values = d.data?.submissionValues || {}
        fireMetaEvent("Lead", {
          email: values.email || values.EMAIL || null,
          phone: values.phone || values.mobilephone || values.PHONE || null,
        })
        return
      }

      // Generic form submission
      if (
        typeof d === "object" &&
        d !== null &&
        (d.event === "form_submitted" || d.type === "form-submitted")
      ) {
        fireMetaEvent("Lead", {
          email: d.email || d.data?.email || null,
          phone: d.phone || d.data?.phone || null,
        })
      }
    }

    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [])

  // Load form script when modal opens
  useEffect(() => {
    if (showModal && formScriptUrl && formContainerRef.current) {
      formContainerRef.current.innerHTML = ""

      const script = document.createElement("script")
      script.src = formScriptUrl
      script.async = true
      script.crossOrigin = "anonymous"

      const urlParts = formScriptUrl.split("/")
      const formId = urlParts[urlParts.length - 2] || "form-script"
      script.id = `fs_${formId}`

      formContainerRef.current.appendChild(script)

      return () => {
        if (formContainerRef.current) {
          formContainerRef.current.innerHTML = ""
        }
      }
    }
  }, [showModal, formScriptUrl])

  const titleText = slice.primary.title?.text || ""

  return (
    <section className="hero-dark" id={idMaker(titleText)}>
      <div className="hero-dark-inner">
        <div className="hero-dark-content">
          <PrismicRichText
            field={slice.primary.title?.richText}
            components={{
              heading1: ({ children }) => (
                <h1 className="hero-dark-title">{children}</h1>
              ),
            }}
          />
          <PrismicRichText
            field={slice.primary.subtitle?.richText}
            components={{
              heading2: ({ children }) => (
                <h2 className="hero-dark-subtitle">{children}</h2>
              ),
              heading3: ({ children }) => (
                <h3 className="hero-dark-subtitle">{children}</h3>
              ),
              paragraph: ({ children }) => (
                <p className="hero-dark-subtitle">{children}</p>
              ),
            }}
          />
          <div className="hero-dark-links">
            {slice.primary.primary_button_label?.text && (
              <button
                type="button"
                className="btn-st hero-dark-btn-primary"
                onClick={handleOpenModal}
              >
                {slice.primary.primary_button_label.text}
              </button>
            )}
            {slice.primary.secondary_button_link?.url && (
              <PrismicLink
                field={slice.primary.secondary_button_link}
                className="hero-dark-btn-secondary"
              >
                {slice.primary.secondary_button_label?.text}
              </PrismicLink>
            )}
          </div>
        </div>

        {(slice.primary.image?.gatsbyImageData || slice.primary.image?.url) && (
          <div className="hero-dark-image-wrap">
            {slice.primary.image?.gatsbyImageData ? (
              <GatsbyImage
                className="hero-dark-image filter-st-3"
                image={slice.primary.image.gatsbyImageData}
                alt={slice.primary.image.alt || ""}
                objectFit="contain"
              />
            ) : (
              <img
                className="hero-dark-image filter-st-3"
                src={slice.primary.image.url}
                alt={slice.primary.image.alt || ""}
              />
            )}
          </div>
        )}
      </div>

      {/* Modal with Embedded Form */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        size="xl"
        className="hero-video-modal"
        aria-labelledby="hero-dark-modal-title"
      >
        <Modal.Header closeButton />
        <Modal.Body className="p-0">
          <div className="hero-video-modal-layout">
            <div className="hero-video-modal-pitch">
              <h2 id="hero-dark-modal-title" className="hero-video-modal-pitch-title">
                {slice.primary.modal_pitch_title || "Here to Simplify Your Operation"}
              </h2>
              <p className="hero-video-modal-pitch-subtitle">
                {slice.primary.modal_pitch_subtitle || "Take the hassle out of running your operations. Our team will show you how GrubPOS optimises your workflow, increases efficiency, and supports your growth."}
              </p>
              <p className="hero-video-modal-pitch-trust">
                {slice.primary.modal_trust_copy || "Over 1,000+ restaurants worldwide rely on GrubPOS."}
              </p>
            </div>
            <div className="hero-video-modal-form-panel">
              <div
                ref={formContainerRef}
                className="hero-video-form-container"
              />
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </section>
  )
}

export const query = graphql`
  fragment HomepageDataBodyHeroDark on PrismicHomepageDataBodyHeroDark {
    id
    slice_type
    primary {
      title {
        richText
        text
      }
      subtitle {
        richText
        text
      }
      primary_button_label {
        richText
        text
      }
      secondary_button_label {
        richText
        text
      }
      secondary_button_link {
        url
        target
        link_type
      }
      modal_pitch_title
      modal_pitch_subtitle
      modal_trust_copy
      form_embed_url {
        url
      }
      image {
        gatsbyImageData(imgixParams: { auto: "", fit: "" })
        alt
        url
      }
    }
  }
`
